import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import AdminProjectService from '../services/AdminProjectService';
import IBaseState from '../../types/IBaseState';
import ProjectPackage from '../types/project/ProjectPackage';
import ProjectTranslation from '../types/project/ProjectTranslation';
import IAdminProject, { ProjectStates } from '../types/IAdminProject';
import AdminProjectPhase from '../types/project/AdminProjectPhase';
import ProjectJobMatchQuestionnaire from '../types/jobMatch/ProjectJobMatchQuestionnaire';
import ProjectJobMatchScale from '../types/jobMatch/ProjectJobMatchScale';
import UploadState from '../../types/UploadState';
import AdminInviteService from '../services/AdminInviteService';
import ProjectInvitation from '../types/invitation/ProjectInvitation';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import AdminProjectPhases from '../constants/AdminProjectPhases';

const NAME = 'AdminProject';

function isAdminProjectPhase(phase: unknown): phase is AdminProjectPhase {
    return (
        phase === AdminProjectPhases.Preparation ||
        phase === AdminProjectPhases.Execution ||
        phase === AdminProjectPhases.Evaluation
    );
}

const createProject = createNotifiedThunk(
    `${NAME}/createProject`,
    AdminProjectService.createProject,
    { error: 'fe.admin.error.create-project' },
);

const getProject = createNotifiedThunk(
    `${NAME}/getProject`,
    AdminProjectService.getProject,
    { error: 'fe.admin.error.fetch-settings' },
);
const saveProject = createNotifiedThunk(
    `${NAME}/saveProject`,
    AdminProjectService.saveProject,
    { error: 'fe.admin.error.save-settings' },
);

type ChangeProjectPhasePayload = {
    productId: number;
    projectId: number;
    phase: number;
    // Not sent to the API: lets a failed request revert the optimistic update.
    previousPhase: AdminProjectPhase;
};

const changeProjectPhase = createNotifiedThunk(
    `${NAME}/changeProjectPhase`,
    ({ productId, projectId, phase }: ChangeProjectPhasePayload) =>
        AdminProjectService.saveProject({ productId, projectId, phase }),
    { error: 'fe.admin.error.save-settings' },
);

const archiveProject = createNotifiedThunk(
    `${NAME}/archiveProject`,
    AdminProjectService.archiveProject,
    { error: 'fe.admin.error.archive-project' },
);

const unarchiveProject = createNotifiedThunk(
    `${NAME}/unarchiveProject`,
    AdminProjectService.unarchiveProject,
    { error: 'fe.admin.error.unarchive-project' },
);

const getPackages = createNotifiedThunk(
    `${NAME}/getPackages`,
    AdminProjectService.getPackages,
    { error: 'fe.admin.error.load-packages' },
);
const selectPackage = createNotifiedThunk(
    `${NAME}/selectPackage`,
    AdminProjectService.selectPackage,
    { error: 'fe.admin.error.select-package' },
);

const getQuestionnaires = createNotifiedThunk(
    `${NAME}/getQuestionnaires`,
    AdminProjectService.getQuestionnaires,
    { error: 'fe.admin.error.load-questionnaires' },
);
const selectQuestionnaires = createNotifiedThunk(
    `${NAME}/selectQuestionnaires`,
    AdminProjectService.selectQuestionnaires,
    { error: 'fe.admin.error.save-questionnaires' },
);
const updateQuestionnaires = createNotifiedThunk(
    `${NAME}/updateQuestionnaires`,
    AdminProjectService.updateQuestionnaires,
    { error: 'fe.admin.error.save-questionnaires' },
);

const getTranslations = createNotifiedThunk(
    `${NAME}/getTranslations`,
    AdminProjectService.getTranslations,
    { error: 'fe.admin.error.load-translations' },
);
const addLanguage = createNotifiedThunk(
    `${NAME}/addLanguage`,
    AdminProjectService.addLanguage,
    { error: 'fe.admin.error.save-language' },
);
const removeLanguage = createNotifiedThunk(
    `${NAME}/removeLanguage`,
    AdminProjectService.removeLanguage,
    { error: 'fe.admin.error.remove-language' },
);
const saveTranslations = createNotifiedThunk(
    `${NAME}/saveTranslations`,
    AdminProjectService.saveTranslations,
    { error: 'fe.admin.error.save-translation' },
);
const saveTranslation = createNotifiedThunk(
    `${NAME}/saveTranslation`,
    AdminProjectService.saveTranslation,
    { error: 'fe.admin.error.save-translation' },
);
const getQuestionnaireScales = createNotifiedThunk(
    `${NAME}/getQuestionnaireScales`,
    AdminProjectService.getQuestionnaireScales,
    { error: 'fe.admin.error.load-scales' },
);

const updateQuestionnaireScales = createNotifiedThunk(
    `${NAME}/updateQuestionnaireScales`,
    AdminProjectService.updateQuestionnaireScales,
    { error: 'fe.admin.error.update-scale' },
);

const getProjectStatistics = createNotifiedThunk(
    `${NAME}/getProjectStatistics`,
    AdminProjectService.getProjectStatistics,
    { error: 'fe.admin.error.fetch-statistics' },
);

const getInvites = createNotifiedThunk(
    `${NAME}/getInvites`,
    AdminInviteService.getInvites,
    { error: 'fe.admin.error.fetch-invitations' },
);

const deleteProject = createNotifiedThunk(
    `${NAME}/deleteProject`,
    AdminProjectService.deleteProject,
    { error: 'fe.admin.error.delete-project' },
);
const copyProject = createNotifiedThunk(
    `${NAME}/copyProject`,
    AdminProjectService.copyProject,
    { error: 'fe.admin.error.save-project' },
);
const anonymizeProject = createNotifiedThunk(
    `${NAME}/anonymizeProject`,
    AdminProjectService.anonymizeProject,
    { error: 'fe.admin.error.anonymize-project' },
);

const autoTranslate = createNotifiedThunk(
    `${NAME}/autoTranslate`,
    AdminProjectService.autoTranslate,
    { error: 'fe.admin.error.auto-translate' },
);
const autoTranslateGroups = createNotifiedThunk(
    `${NAME}/autoTranslateGroups`,
    AdminProjectService.autoTranslateGroups,
    { error: 'fe.admin.error.auto-translate' },
);
const getProjectRecap = createNotifiedThunk(
    `${NAME}/getProjectRecap`,
    AdminProjectService.getProject,
    { error: 'fe.admin.error.fetch-project-recap' },
);

export type AdminProjectState = {
    project: UploadState<IAdminProject | null>;
    packages: IBaseState<ProjectPackage[]>;
    questionnaires: UploadState<ProjectJobMatchQuestionnaire[]>;
    translations: IBaseState<{ [key: string]: ProjectTranslation[] } | null>;
    scales: IBaseState<{ [key: number]: IBaseState<ProjectJobMatchScale[]> }>;
    statistics: IBaseState<any | null>;
    invites: IBaseState<ProjectInvitation[]>;
    recap: IBaseState<IAdminProject | null>;
};

const initialState: AdminProjectState = {
    project: { data: null, loading: false, uploading: false },
    packages: { data: [] },
    questionnaires: { data: [], uploading: false },
    translations: { data: null },
    scales: { data: {} },
    statistics: { data: null, loading: false },
    invites: { data: [], loading: false },
    recap: { data: null, loading: false },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(createProject.pending, (state: AdminProjectState) => {
            state.project.loading = true;
        });
        builder.addCase(
            createProject.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload
                    .data as IAdminProject | null;
                state.project.loading = false;
            },
        );
        builder.addCase(createProject.rejected, (state: AdminProjectState) => {
            state.project.loading = false;
        });

        builder.addCase(getPackages.pending, (state: AdminProjectState) => {
            state.packages.loading = true;
            state.packages.data = [];
        });
        builder.addCase(
            getPackages.fulfilled,
            (state: AdminProjectState, action) => {
                state.packages.data = action.payload.data;
                state.packages.loading = false;
            },
        );
        builder.addCase(getPackages.rejected, (state: AdminProjectState) => {
            state.packages.loading = false;
        });

        builder.addCase(getProject.pending, (state: AdminProjectState) => {
            state.project.loading = true;
            state.project.data = null;
        });
        builder.addCase(
            getProject.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload
                    .data as IAdminProject | null;
                state.project.loading = false;
            },
        );
        builder.addCase(getProject.rejected, (state: AdminProjectState) => {
            state.project.loading = false;
            state.project.data = null;
        });

        builder.addCase(
            getQuestionnaires.pending,
            (state: AdminProjectState) => {
                state.questionnaires.loading = true;
                state.questionnaires.data = [];
            },
        );
        builder.addCase(
            getQuestionnaires.fulfilled,
            (state: AdminProjectState, action) => {
                state.questionnaires.data = action.payload.data;
            },
        );

        builder.addCase(getTranslations.pending, (state: AdminProjectState) => {
            state.translations.loading = true;
            state.translations.data = null;
        });
        builder.addCase(
            getTranslations.fulfilled,
            (state: AdminProjectState, action) => {
                if (!state.translations.data) {
                    state.translations.data = {};
                }

                state.translations.data[action.meta.arg.locale] =
                    action.payload.data;
            },
        );
        builder.addCase(
            saveTranslations.fulfilled,
            (state: AdminProjectState, action) => {
                const { locale } = action.meta.arg;

                if (state.translations.data?.[locale]) {
                    // state.translations.data[locale] = ;
                }
            },
        );

        builder.addCase(
            addLanguage.fulfilled,
            (state: AdminProjectState, action) => {
                if (state.project.data) {
                    const data = action.payload.data as { locales: string[] };
                    state.project.data.locales = data.locales;
                }
            },
        );

        builder.addCase(
            removeLanguage.fulfilled,
            (state: AdminProjectState, action) => {
                if (state.project.data) {
                    const data = action.payload.data as { locales: string[] };
                    state.project.data.locales = data.locales;
                }
            },
        );

        builder.addCase(
            getQuestionnaireScales.pending,
            (state: AdminProjectState, action) => {
                const { questionnaireId } = action.meta.arg;

                state.scales.data[questionnaireId] = {
                    loading: true,
                    data: [],
                };
            },
        );
        builder.addCase(
            getQuestionnaireScales.fulfilled,
            (state: AdminProjectState, action) => {
                const { questionnaireId } = action.meta.arg;
                if (state.scales.data?.[questionnaireId]?.data) {
                    state.scales.data[questionnaireId].data =
                        action.payload.data;
                }
            },
        );
        builder.addCase(
            updateQuestionnaireScales.fulfilled,
            (state: AdminProjectState, action) => {
                const { questionnaireId, scaleId } = action.meta.arg;
                if (state.scales.data?.[questionnaireId]?.data?.[scaleId]) {
                    state.scales.data[questionnaireId].data[scaleId] =
                        action.payload.data;
                }
            },
        );

        builder.addCase(
            selectQuestionnaires.pending,
            (state: AdminProjectState) => {
                state.questionnaires.uploading = true;
            },
        );
        builder.addCase(
            selectQuestionnaires.fulfilled,
            (state: AdminProjectState, action) => {
                state.project.data = action.payload.data;
            },
        );
        builder.addCase(
            updateQuestionnaires.fulfilled,
            (state: AdminProjectState, action) => {
                state.questionnaires.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectStatistics.pending,
            (state: AdminProjectState) => {
                state.statistics.loading = true;
                state.statistics.data = null;
            },
        );
        builder.addCase(
            getProjectStatistics.fulfilled,
            (state: AdminProjectState, action) => {
                state.statistics.data = action.payload.data;
            },
        );

        builder.addCase(
            changeProjectPhase.pending,
            (state: AdminProjectState, action) => {
                const { projectId, phase } = action.meta.arg;

                if (
                    state.project.data?.id === projectId &&
                    isAdminProjectPhase(phase)
                ) {
                    state.project.data.phase = phase;
                }
            },
        );
        builder.addCase(
            changeProjectPhase.fulfilled,
            (state: AdminProjectState, action) => {
                const { projectId } = action.meta.arg;
                const savedPhase = action.payload.data?.phase;

                if (
                    state.project.data?.id === projectId &&
                    isAdminProjectPhase(savedPhase)
                ) {
                    state.project.data.phase = savedPhase;
                }
            },
        );
        builder.addCase(
            changeProjectPhase.rejected,
            (state: AdminProjectState, action) => {
                const { projectId, previousPhase } = action.meta.arg;

                if (state.project.data?.id === projectId) {
                    state.project.data.phase = previousPhase;
                }
            },
        );
        builder.addCase(archiveProject.pending, (state: AdminProjectState) => {
            state.project.uploading = true;
        });
        builder.addCase(
            unarchiveProject.pending,
            (state: AdminProjectState) => {
                state.project.uploading = true;
            },
        );

        builder.addCase(getInvites.pending, (state: AdminProjectState) => {
            state.invites.loading = true;
            state.invites.data = [];
        });
        builder.addCase(
            getInvites.fulfilled,
            (state: AdminProjectState, action) => {
                state.invites.data = action.payload.data;
            },
        );
        builder.addCase(getInvites.rejected, (state: AdminProjectState) => {
            state.invites.data = [];
        });

        builder.addCase(getProjectRecap.pending, (state: AdminProjectState) => {
            state.recap.loading = true;
        });
        builder.addCase(
            getProjectRecap.fulfilled,
            (state: AdminProjectState, action) => {
                state.recap.data = action.payload.data as IAdminProject | null;
                state.recap.loading = false;
            },
        );
        builder.addCase(
            getProjectRecap.rejected,
            (state: AdminProjectState) => {
                state.recap.loading = false;
                state.recap.data = null;
            },
        );

        builder.addCase(
            archiveProject.fulfilled,
            (state: AdminProjectState, action) => {
                const { projectId } = action.meta.arg;

                if (state.project.data?.id === projectId) {
                    state.project.data.state = ProjectStates.Archived;
                }
                if (state.recap.data?.id === projectId) {
                    state.recap.data.state = ProjectStates.Archived;
                }
            },
        );
        builder.addCase(
            unarchiveProject.fulfilled,
            (state: AdminProjectState, action) => {
                const { projectId } = action.meta.arg;

                if (state.project.data?.id === projectId) {
                    state.project.data.state = ProjectStates.Default;
                }
                if (state.recap.data?.id === projectId) {
                    state.recap.data.state = ProjectStates.Default;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getQuestionnaires.fulfilled, getQuestionnaires.rejected),
            (state: AdminProjectState) => {
                state.questionnaires.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                selectQuestionnaires.fulfilled,
                selectQuestionnaires.rejected,
            ),
            (state: AdminProjectState) => {
                state.questionnaires.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getTranslations.fulfilled, getTranslations.rejected),
            (state: AdminProjectState) => {
                state.translations.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getQuestionnaireScales.fulfilled,
                getQuestionnaireScales.rejected,
            ),
            (state: AdminProjectState, action) => {
                const { questionnaireId } = action.meta.arg;

                if (state.scales.data?.[questionnaireId]) {
                    state.scales.data[questionnaireId].loading = false;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(
                getProjectStatistics.fulfilled,
                getProjectStatistics.rejected,
            ),
            (state: AdminProjectState) => {
                state.statistics.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(saveProject.pending, changeProjectPhase.pending),
            (state: AdminProjectState) => {
                state.project.uploading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                saveProject.fulfilled,
                saveProject.rejected,
                changeProjectPhase.fulfilled,
                changeProjectPhase.rejected,
                archiveProject.fulfilled,
                archiveProject.rejected,
                unarchiveProject.fulfilled,
                unarchiveProject.rejected,
            ),
            (state: AdminProjectState) => {
                state.project.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getInvites.fulfilled, getInvites.rejected),
            (state: AdminProjectState) => {
                state.invites.loading = false;
            },
        );
    },
});

const state = (state: AppState) => state.admin.project;

const AdminProjectSlice = {
    reducer: slice.reducer,
    actions: {
        createProject,
        getProject,
        saveProject,
        changeProjectPhase,
        archiveProject,
        unarchiveProject,
        selectPackage,
        getPackages,
        getQuestionnaires,
        selectQuestionnaires,
        getTranslations,
        addLanguage,
        removeLanguage,
        saveTranslation,
        saveTranslations,
        getQuestionnaireScales,
        updateQuestionnaireScales,
        updateQuestionnaires,
        getProjectStatistics,
        getInvites,
        deleteProject,
        copyProject,
        anonymizeProject,
        autoTranslate,
        autoTranslateGroups,
        getProjectRecap,
    },
    state: {
        getAll: state,
        getProject: createSelector(
            state,
            (state: AdminProjectState) => state.project,
        ),
        getPackages: createSelector(
            state,
            (state: AdminProjectState) => state.packages,
        ),
        getQuestionnaires: createSelector(
            state,
            (state: AdminProjectState) => state.questionnaires,
        ),
        getTranslations: createSelector(
            state,
            (state: AdminProjectState) => state.translations,
        ),
        getTranslationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: AdminProjectState, locale: string) =>
                state.translations?.data?.[locale],
        ),
        getQuestionnaireScales: createSelector(
            state,
            (state: AdminProjectState) => state.scales,
        ),
        getQuestionnaireScalesByQuestionnaireId: createSelector(
            [
                state,
                (state: AppState, questionnaireId: number) => questionnaireId,
            ],
            (state: AdminProjectState, questionnaireId: number) =>
                state.scales?.data?.[questionnaireId],
        ),
        getProjectStatistics: createSelector(
            state,
            (state: AdminProjectState) => state.statistics,
        ),
        getInvites: createSelector(
            state,
            (state: AdminProjectState) => state.invites,
        ),
        getRecap: createSelector(
            state,
            (state: AdminProjectState) => state.recap,
        ),
    },
};

export default AdminProjectSlice;
